import { useState, useEffect, useCallback } from 'react';
import { UserPlus, RefreshCw, Save, Calendar, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Şubat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayıs' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Ağustos' },
  { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasım' },
  { value: 12, label: 'Aralık' }
];

const YEARS = [2025, 2026];

export default function LeadEntry() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [monthlyLeads, setMonthlyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [leadCounts, setLeadCounts] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [personnelRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/api/spreadsheet-personnel`, getAuthHeader()),
        axios.get(`${API_URL}/api/leads/monthly`, getAuthHeader())
      ]);
      setUsers(personnelRes.data);
      setMonthlyLeads(leadsRes.data);
      
      // Set default user
      if (personnelRes.data.length > 0 && !selectedUser) {
        setSelectedUser(personnelRes.data[0].email);
      }
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load lead counts for selected user and year
  useEffect(() => {
    if (selectedUser && selectedYear) {
      const userLeads = monthlyLeads.filter(
        l => l.user_email === selectedUser.toLowerCase() && l.year === selectedYear
      );
      const counts = {};
      userLeads.forEach(l => {
        counts[l.month] = l.lead_count;
      });
      setLeadCounts(counts);
    }
  }, [selectedUser, selectedYear, monthlyLeads]);

  const handleSaveMonth = async (month) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/leads/monthly`, {
        user_email: selectedUser,
        year: selectedYear,
        month: month,
        lead_count: parseInt(leadCounts[month]) || 0
      }, getAuthHeader());
      toast.success(`${MONTHS.find(m => m.value === month)?.label} ${selectedYear} kaydedildi`);
      fetchData();
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      for (const month of MONTHS) {
        if (leadCounts[month.value] !== undefined) {
          await axios.post(`${API_URL}/api/leads/monthly`, {
            user_email: selectedUser,
            year: selectedYear,
            month: month.value,
            lead_count: parseInt(leadCounts[month.value]) || 0
          }, getAuthHeader());
        }
      }
      toast.success('Tüm aylar kaydedildi');
      fetchData();
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const [yearlyLeadInput, setYearlyLeadInput] = useState('');
  
  const handleSaveYearly = async () => {
    const yearlyTotal = parseInt(yearlyLeadInput) || 0;
    if (yearlyTotal <= 0) {
      toast.error('Lütfen geçerli bir yıllık veri sayısı girin');
      return;
    }
    
    setSaving(true);
    try {
      // Distribute yearly total equally across 12 months
      const monthlyAmount = Math.floor(yearlyTotal / 12);
      const remainder = yearlyTotal % 12;
      
      for (let i = 0; i < 12; i++) {
        const monthValue = i + 1;
        // Add 1 extra to first 'remainder' months to distribute evenly
        const leadCount = monthlyAmount + (i < remainder ? 1 : 0);
        
        await axios.post(`${API_URL}/api/leads/monthly`, {
          user_email: selectedUser,
          year: selectedYear,
          month: monthValue,
          lead_count: leadCount
        }, getAuthHeader());
      }
      
      toast.success(`Yıllık ${yearlyTotal} veri 12 aya eşit dağıtıldı (${monthlyAmount}${remainder > 0 ? ` + ilk ${remainder} aya +1` : ''} data/ay)`);
      setYearlyLeadInput('');
      fetchData();
    } catch (error) {
      toast.error('Yıllık dağıtım başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleLeadCountChange = (month, value) => {
    setLeadCounts(prev => ({ ...prev, [month]: value }));
  };

  // Calculate totals for selected user
  const userTotalLeads = monthlyLeads
    .filter(l => l.user_email === selectedUser.toLowerCase())
    .reduce((sum, l) => sum + (l.lead_count || 0), 0);

  const yearTotalLeads = monthlyLeads
    .filter(l => l.user_email === selectedUser.toLowerCase() && l.year === selectedYear)
    .reduce((sum, l) => sum + (l.lead_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="lead-entry-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-entry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Lead Girişi</h2>
          <p className="text-muted-foreground">E-tablolardaki personeller adına aylık lead adetlerini girin</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="secondary" className="py-2 px-3">
            {users.length} Personel
          </Badge>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-leads">
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Selection Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Dönem ve Personel Seçimi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Personel</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="user-select">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.email} value={user.email}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Yıl</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Özet</Label>
              <div className="flex gap-2">
                <Badge variant="secondary" className="py-2">
                  Yıl: {yearTotalLeads} lead
                </Badge>
                <Badge variant="outline" className="py-2">
                  Toplam: {userTotalLeads} lead
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Yearly Lead Entry */}
      <Card className="border-2 border-dashed border-primary/30">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2 text-primary">
            <Calendar className="h-5 w-5" />
            Yıllık Toplu Veri Girişi
          </CardTitle>
          <CardDescription>
            Yıllık toplam veri sayısı girin, sistem otomatik olarak 12 aya eşit dağıtacak
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>Yıllık Toplam Veri</Label>
              <Input
                type="number"
                min="0"
                value={yearlyLeadInput}
                onChange={(e) => setYearlyLeadInput(e.target.value)}
                placeholder="Örn: 1200 (= 100/ay)"
                className="text-lg"
                data-testid="yearly-lead-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">
                {yearlyLeadInput && parseInt(yearlyLeadInput) > 0 
                  ? `≈ ${Math.floor(parseInt(yearlyLeadInput) / 12)} veri/ay`
                  : 'Hesaplama bekleniyor'}
              </Label>
              <Button
                onClick={handleSaveYearly}
                disabled={saving || !yearlyLeadInput || parseInt(yearlyLeadInput) <= 0}
                className="w-full sm:w-auto"
                data-testid="save-yearly-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {selectedYear} Yılına Dağıt
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Not: Mevcut aylık veriler üzerine yazılacaktır. Örnek: 1200 veri → 12 aya eşit dağılır (100 veri/ay)
          </p>
        </CardContent>
      </Card>

      {/* Monthly Lead Entry Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {selectedYear} Yılı Aylık Lead Girişi
          </CardTitle>
          <CardDescription>
            {users.find(u => u.email === selectedUser)?.name || selectedUser} için aylık lead adetlerini girin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {MONTHS.map((month) => (
              <div key={month.value} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{month.label}</span>
                  <Badge variant="outline" className="text-xs">
                    {selectedYear}
                  </Badge>
                </div>
                <Input
                  type="number"
                  min="0"
                  value={leadCounts[month.value] || ''}
                  onChange={(e) => handleLeadCountChange(month.value, e.target.value)}
                  placeholder="0"
                  className="text-center"
                  data-testid={`lead-input-${month.value}`}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSaveMonth(month.value)}
                  disabled={saving}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Kaydet
                </Button>
              </div>
            ))}
          </div>
          
          <div className="mt-6 flex justify-end">
            <Button onClick={handleSaveAll} disabled={saving} data-testid="save-all-btn">
              {saving ? (
                <>
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Tüm Ayları Kaydet
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* All Users Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Personel Lead Özeti
          </CardTitle>
          <CardDescription>Tüm personellerin aylık lead dağılımı</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="sticky left-0 bg-muted/50">Personel</TableHead>
                  {MONTHS.map(m => (
                    <TableHead key={m.value} className="text-center min-w-[60px]">
                      {m.label.substring(0, 3)}
                    </TableHead>
                  ))}
                  <TableHead className="text-center font-bold">Toplam</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => {
                  const userLeads = monthlyLeads.filter(
                    l => l.user_email === user.email.toLowerCase() && l.year === selectedYear
                  );
                  const leadsMap = {};
                  userLeads.forEach(l => { leadsMap[l.month] = l.lead_count; });
                  const total = Object.values(leadsMap).reduce((s, v) => s + (v || 0), 0);
                  
                  return (
                    <TableRow key={user.email}>
                      <TableCell className="sticky left-0 bg-background font-medium">
                        {user.name}
                      </TableCell>
                      {MONTHS.map(m => (
                        <TableCell key={m.value} className="text-center">
                          {leadsMap[m.value] || '-'}
                        </TableCell>
                      ))}
                      <TableCell className="text-center font-bold">{total}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
